import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Camera, Heart, Calendar, Scale, Hash, AlertCircle, Loader2, User } from 'lucide-react';
import { petsService, Pet } from '../../lib/petsService';
import { AddPetModal } from './AddPetModal';
import { EditPetModal } from './EditPetModal';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

export function PetProfiles() {
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [deletingPetId, setDeletingPetId] = useState<string | null>(null);

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await petsService.getPets();
      setPets(data);
    } catch (err) {
      console.error('Error fetching pets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pets');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeletePet = async (petId: string) => {
    if (!window.confirm('Are you sure you want to delete this pet? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingPetId(petId);
      await petsService.deletePet(petId);
      setPets(pets.filter(pet => pet.id !== petId));
    } catch (err) {
      console.error('Error deleting pet:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete pet');
    } finally {
      setDeletingPetId(null);
    }
  };

  const handleEditPet = (pet: Pet) => {
    setSelectedPet(pet);
    setShowEditModal(true);
  };

  const handleAddSuccess = () => {
    fetchPets();
    setShowAddModal(false);
  };

  const handleEditSuccess = () => {
    fetchPets();
    setShowEditModal(false);
    setSelectedPet(null);
  };

  const getPetColor = (species: string) => {
    const colors: { [key: string]: string } = {
      dog: 'bg-amber-50 text-amber-600 border-amber-200',
      cat: 'bg-purple-50 text-purple-600 border-purple-200',
      bird: 'bg-blue-50 text-blue-600 border-blue-200',
      fish: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      rabbit: 'bg-pink-50 text-pink-600 border-pink-200',
      hamster: 'bg-orange-50 text-orange-600 border-orange-200',
      guinea_pig: 'bg-green-50 text-green-600 border-green-200',
      ferret: 'bg-indigo-50 text-indigo-600 border-indigo-200',
      horse: 'bg-red-50 text-red-600 border-red-200',
      reptile: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      default: 'bg-gray-50 text-gray-600 border-gray-200'
    };
    return colors[species.toLowerCase()] || colors.default;
  };

  const calculateAge = (birthDate: string | null): string => {
    if (!birthDate) return 'Unknown';
    
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInYears = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return `${ageInYears - 1} years`;
    }
    
    if (ageInYears === 0) {
      const ageInMonths = Math.floor((today.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
      return `${ageInMonths} months`;
    }
    
    return `${ageInYears} years`;
  };

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pet Profiles</h2>
          <p className="text-gray-600 mt-1">Manage your beloved pets' information</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={20} className="mr-2" />
          Add Pet
        </Button>
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200" />
              <div className="p-6 space-y-3">
                <div className="h-6 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-4 bg-gray-100 rounded w-1/3" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {!loading && pets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map((pet) => (
            <div key={pet.id}>
              <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-50">
                  {pet.image_url ? (
                    <img
                      src={pet.image_url}
                      alt={pet.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera size={48} className="text-gray-400" />
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="absolute top-3 right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditPet(pet)}
                      className="bg-white/90 text-gray-700 hover:bg-white"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePet(pet.id)}
                      disabled={deletingPetId === pet.id}
                      className="bg-white/90 text-red-600 hover:bg-white hover:text-red-700"
                    >
                      {deletingPetId === pet.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </Button>
                  </div>

                  {/* Species Badge */}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium border ${getPetColor(pet.species)}`}>
                    {pet.species.charAt(0).toUpperCase() + pet.species.slice(1)}
                  </div>
                </div>

                {/* Pet Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900">{pet.name}</h3>
                    <Heart size={20} className="text-red-500" />
                  </div>

                  <div className="space-y-3">
                    {pet.breed && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium mr-2">Breed:</span>
                        <span>{pet.breed}</span>
                      </div>
                    )}

                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar size={16} className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Age:</span>
                      <span>{calculateAge(pet.birth_date)}</span>
                    </div>

                    {pet.weight && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Scale size={16} className="mr-2 text-gray-400" />
                        <span className="font-medium mr-2">Weight:</span>
                        <span>{pet.weight} kg</span>
                      </div>
                    )}

                    {pet.color && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium mr-2">Color:</span>
                        <span>{pet.color}</span>
                      </div>
                    )}

                    {pet.microchip_number && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Hash size={16} className="mr-2 text-gray-400" />
                        <span className="font-medium mr-2">Microchip:</span>
                        <span className="font-mono text-xs">{pet.microchip_number}</span>
                      </div>
                    )}

                    {pet.notes && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-600 line-clamp-2">{pet.notes}</p>
                      </div>
                    )}

                    {/* Guardian Information */}
                    {(pet.guardian_name || pet.guardian_phone || pet.guardian_email) && (
                      <div className="pt-3 border-t border-gray-100">
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <User size={16} className="mr-2 text-gray-400" />
                          <span className="font-medium">Guardian:</span>
                        </div>
                        {pet.guardian_name && (
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Name:</span> {pet.guardian_name}
                            {pet.guardian_relationship && (
                              <span className="text-gray-500 ml-1">({pet.guardian_relationship})</span>
                            )}
                          </p>
                        )}
                        {pet.guardian_phone && (
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Phone:</span> {pet.guardian_phone}
                          </p>
                        )}
                        {pet.guardian_email && (
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Email:</span> {pet.guardian_email}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      ) : !loading ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pets yet</h3>
          <p className="text-gray-600 mb-6">Start by adding your first pet to keep track of their information</p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus size={20} className="mr-2" />
            Add Your First Pet
          </Button>
        </div>
      ) : null}

      {/* Add Pet Modal */}
      <AddPetModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Edit Pet Modal */}
      {selectedPet && (
        <EditPetModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPet(null);
          }}
          pet={selectedPet}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}